import React from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import ResultsPage from '@/pages/ResultsPage';
import { StaticRouteConfig, LazyRouteConfig } from './types';

// Redirect components for backward compatibility
const ThankYouRedirect: React.FC = () => {
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  
  if (id) {
    return <Navigate to={`/results/${id}`} replace />;
  }
  
  // No ID - redirect to home
  return <Navigate to="/" replace />;
};

const CognitiveThankYouRedirect: React.FC = () => {
  // Try to get submission ID from localStorage
  try {
    const cognitiveResults = localStorage.getItem('cognitiveResults');
    if (cognitiveResults) {
      const parsed = JSON.parse(cognitiveResults);
      // Check for behavioral submission in dualAssessmentResults
      const dualResults = localStorage.getItem('dualAssessmentResults');
      if (dualResults) {
        const dualParsed = JSON.parse(dualResults);
        if (dualParsed.submissionId) {
          return <Navigate to={`/results/${dualParsed.submissionId}`} replace />;
        }
      }
    }
  } catch (e) {
    console.error('Error parsing localStorage for redirect:', e);
  }
  
  // Fallback to home
  return <Navigate to="/" replace />;
};

const CognitiveOnlyThankYouRedirect: React.FC = () => {
  // For cognitive-only, try to get the submission ID
  try {
    const cognitiveResults = localStorage.getItem('cognitiveResults');
    if (cognitiveResults) {
      const parsed = JSON.parse(cognitiveResults);
      if (parsed.submissionId) {
        return <Navigate to={`/results/${parsed.submissionId}`} replace />;
      }
    }
  } catch (e) {
    console.error('Error parsing localStorage for redirect:', e);
  }
  
  // Fallback to home
  return <Navigate to="/" replace />;
};

// Model results redirects
const Model1ResultsRedirect: React.FC = () => {
  const urlParts = window.location.pathname.split('/');
  const submissionId = urlParts[urlParts.length - 1];
  
  if (submissionId && submissionId !== 'results') {
    return <Navigate to={`/results/${submissionId}`} replace />;
  }
  
  return <Navigate to="/" replace />;
};

const Model2ResultsRedirect: React.FC = () => {
  const urlParts = window.location.pathname.split('/');
  const submissionId = urlParts[urlParts.length - 1];
  
  if (submissionId && submissionId !== 'results') {
    return <Navigate to={`/results/${submissionId}`} replace />;
  }
  
  return <Navigate to="/" replace />;
};

const Model3ResultsRedirect: React.FC = () => {
  const urlParts = window.location.pathname.split('/');
  const submissionId = urlParts[urlParts.length - 1];
  
  if (submissionId && submissionId !== 'results') {
    return <Navigate to={`/results/${submissionId}`} replace />;
  }
  
  return <Navigate to="/" replace />;
};

// Lazy load assessment pages
const BehavioralAssessmentPage = React.lazy(() => import('@/pages/BehavioralAssessmentPage'));
const M1BehavioralAssessmentPage = React.lazy(() => import('@/pages/m1-BehavioralAssessmentPage'));
const M1RegisterPage = React.lazy(() => import('@/pages/M1RegisterPage'));
const M2BehavioralAssessmentPage = React.lazy(() => import('@/pages/m2-BehavioralAssessmentPage'));
const M2RegisterPage = React.lazy(() => import('@/pages/M2RegisterPage'));
const M3BehavioralAssessmentPage = React.lazy(() => import('@/pages/m3-BehavioralAssessmentPage'));
const M3RegisterPage = React.lazy(() => import('@/pages/M3RegisterPage'));
const CognitiveAssessment = React.lazy(() => import('@/pages/CognitiveAssessment'));
const WorkBehavior = React.lazy(() => import('@/pages/WorkBehavior'));
const StandaloneWorkBehavior = React.lazy(() => import('@/pages/StandaloneWorkBehavior'));
const StandaloneFullAssessment = React.lazy(() => import('@/pages/StandaloneFullAssessment'));
const CognitiveStandalone = React.lazy(() => import('@/pages/CognitiveStandalone'));
const IntegrityAssessment = React.lazy(() => import('@/pages/IntegrityAssessment'));
const IntegrityThankYou = React.lazy(() => import('@/pages/IntegrityThankYou'));

export const staticAssessmentRoutes: StaticRouteConfig[] = [
  // Canonical results route
  { path: '/results/:submissionId', component: ResultsPage },
  { path: '/results', component: ResultsPage },
  
  // Legacy redirects for backward compatibility
  { path: '/model1/results/:submissionId', component: Model1ResultsRedirect },
  { path: '/model2/results/:submissionId', component: Model2ResultsRedirect },
  { path: '/m2/results/:submissionId', component: Model2ResultsRedirect },
  { path: '/model3/results/:submissionId', component: Model3ResultsRedirect },
  { path: '/thank-you', component: ThankYouRedirect },
  { path: '/cognitive-thank-you', component: CognitiveThankYouRedirect },
  { path: '/cognitive-only-thank-you', component: CognitiveOnlyThankYouRedirect },
];

export const lazyAssessmentRoutes: LazyRouteConfig[] = [
  { path: '/assessment', component: BehavioralAssessmentPage, skeletonRoute: '/assessment' },
  { path: '/behavioral-assessment', component: BehavioralAssessmentPage, skeletonRoute: '/assessment' },
  { path: '/model1', component: M1BehavioralAssessmentPage, skeletonRoute: '/model1' },
  { path: '/m1-register', component: M1RegisterPage, skeletonRoute: '/m1-register' },
  { path: '/model2', component: M2BehavioralAssessmentPage },
  { path: '/m2-register', component: M2RegisterPage },
  { path: '/model3', component: M3BehavioralAssessmentPage, skeletonRoute: '/model3' },
  { path: '/m3-register', component: M3RegisterPage, skeletonRoute: '/m3-register' },
  { path: '/cognitive', component: CognitiveStandalone },
  { path: '/cognitive-assessment', component: CognitiveAssessment },
  { path: '/cognitive-standalone', component: CognitiveStandalone },
  { path: '/work-behavior', component: WorkBehavior },
  { path: '/work-behavior-standalone', component: StandaloneWorkBehavior },
  { path: '/self-assessment-standalone', component: StandaloneFullAssessment },
  { path: '/integrity', component: IntegrityAssessment },
  { path: '/integrity-thank-you', component: IntegrityThankYou },
];
